(function () {
	"use strict";
	if (window.PartifyTouchupColorDetails) return;
	const cache = new Map();
	const collectionPhotoCache = new Map();
	const paintCodePhotoCache = new Map();
	const query = `query TouchupColorDetails($handle: String!, $after: String, $paintCodesAfter: String, $includeImages: Boolean!, $includePaintCodes: Boolean!) {
		metaobject(handle: {type: "year_make_model_lookup", handle: $handle}) {
			handle
			names: field(key: "paint_code_names") { value }
			images: field(key: "paint_code_images") @include(if: $includeImages) {
				references(first: 100, after: $after) {
					pageInfo { hasNextPage endCursor }
					nodes { ... on Metaobject {
						code: field(key: "paint_code") { value }
						photo: field(key: "images") {
							reference { ... on MediaImage {
								image { url(transform: {maxWidth: 1000}) altText width height }
							} }
						}
					} }
				}
			}
			fallbacks: field(key: "paint_codes") @include(if: $includePaintCodes) {
				references(first: 100, after: $paintCodesAfter) {
					pageInfo { hasNextPage endCursor }
					nodes { ... on Metaobject {
						code: field(key: "paint_code") { value }
						photo: field(key: "paint_code_image") {
							reference { ... on MediaImage {
								image { url(transform: {maxWidth: 1000}) altText width height }
							} }
						}
					} }
				}
			}
		}
	}`;
	function toHandle(value) {
		return String(value || "").toLowerCase().replace(/&/g, "and")
			.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
	}
	function handles(vehicle) {
		vehicle = vehicle || {};
		const base = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean);
		return Array.from(new Set([
			vehicle.fitmentYmm,
			base.length === 3 && (vehicle.submodel || vehicle.engine)
				? base.concat([vehicle.submodel, vehicle.engine]).filter(Boolean).join(" ") : "",
			base.length === 3 && vehicle.submodel ? base.concat(vehicle.submodel).join(" ") : "",
			vehicle.ymm || vehicle.YMM || vehicle.vehicle,
			base.join(" "),
		].map(toHandle).filter(Boolean)));
	}
	function key(vehicle) {
		return handles(vehicle).join("|");
	}
	function codeKey(code) {
		return String(code || "").trim().toUpperCase();
	}
	function normalizeNames(value) {
		const result = Object.create(null);
		let parsed;
		try { parsed = JSON.parse(value || "{}"); } catch (error) { return result; }
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return result;
		Object.keys(parsed).forEach(function (code) {
			const seen = new Set();
			const names = [].concat(parsed[code] || []).filter(function (name) {
				if (typeof name !== "string" || !name.trim()) return false;
				const normalized = name.trim().toLowerCase();
				if (seen.has(normalized) || codeKey(name) === codeKey(code)) return false;
				seen.add(normalized);
				return true;
			}).map(function (name) { return name.trim(); });
			if (names.length) result[codeKey(code)] = names.join(" / ");
		});
		return result;
	}
	function fetchHandle(handle, post, shopKey, namesOnly) {
		const cacheKey = shopKey + "::" + handle + (namesOnly ? "::names" : "");
		if (cache.has(cacheKey)) return cache.get(cacheKey);
		let details = null;
		const imageCursors = new Set();
		const paintCodeCursors = new Set();
		function readImages(refs, images) {
			((refs && refs.nodes) || []).forEach(function (node) {
				const code = codeKey(node && node.code && node.code.value);
				const image = node && node.photo && node.photo.reference && node.photo.reference.image;
				if (code && image && /^https:\/\//i.test(image.url || "") && !images[code]) images[code] = image;
			});
		}
		function nextCursor(refs, seen) {
			const page = refs && refs.pageInfo;
			if (!page || !page.hasNextPage || !page.endCursor || seen.has(page.endCursor)) return null;
			seen.add(page.endCursor);
			return page.endCursor;
		}
		function fetchPage(after, paintCodesAfter, includeImages, includePaintCodes) {
			return Promise.resolve().then(function () {
				return post({query: query, variables: {
					handle: handle, after: after, paintCodesAfter: paintCodesAfter,
					includeImages: includeImages, includePaintCodes: includePaintCodes,
				}});
			}).then(function (json) {
				if (!json || (json.errors && json.errors.length)) throw new Error("Color details unavailable");
				const entry = json.data && json.data.metaobject;
				if (!entry) return null;
				if (!details) details = {
					handle: entry.handle,
					names: normalizeNames(entry.names && entry.names.value),
					images: Object.create(null),
					fallbackImages: Object.create(null),
				};
				const refs = entry.images && entry.images.references;
				const fallbacks = entry.fallbacks && entry.fallbacks.references;
				if (includeImages) readImages(refs, details.images);
				if (includePaintCodes) readImages(fallbacks, details.fallbackImages);
				const imageAfter = includeImages ? nextCursor(refs, imageCursors) : null;
				const fallbackAfter = includePaintCodes ? nextCursor(fallbacks, paintCodeCursors) : null;
				if (imageAfter || fallbackAfter) {
					return fetchPage(imageAfter, fallbackAfter, !!imageAfter, !!fallbackAfter);
				}
				return details;
			});
		}
		const promise = fetchPage(null, null, !namesOnly, !namesOnly).catch(function (error) {
			cache.delete(cacheKey);
			throw error;
		});
		cache.set(cacheKey, promise);
		return promise;
	}
	function loadNames(vehicle, post, shopKey) {
		const candidates = handles(vehicle);
		function next(index) {
			if (index >= candidates.length) return Promise.resolve(null);
			return fetchHandle(candidates[index], post, shopKey || "", true).then(function (details) {
				return details && Object.keys(details.names).length ? details : next(index + 1);
			});
		}
		return next(0).catch(function () { return null; });
	}
	function load(vehicle, post, shopKey, selectedCode) {
		const candidates = handles(vehicle);
		const requestedCode = codeKey(selectedCode);
		function next(index) {
			if (index >= candidates.length) return Promise.resolve(null);
			return fetchHandle(candidates[index], post, shopKey || "").then(function (details) { return details || next(index + 1); });
		}
		return next(0).catch(function () { return null; }).then(function (details) {
			// A selected code can have a backup even when the vehicle has no lookup yet.
			if (!details && requestedCode) details = {handle: candidates[0] || "", names: {}, images: {}, fallbackImages: {}};
			return details ? addCollectionPhotos(details, vehicle, post, shopKey || "", requestedCode) : null;
		}).then(function (details) {
			if (details) Object.keys(details.fallbackImages).forEach(function (code) {
				if (!details.images[code]) details.images[code] = details.fallbackImages[code];
			});
			if (!details || !requestedCode || details.images[requestedCode]) return details;
			return fetchPaintCodePhoto(requestedCode, post, shopKey || "").then(function (photo) {
				if (photo) details.images[requestedCode] = photo;
				return details;
			}).catch(function () { return details; });
		}).catch(function () { return null; });
	}
	function fetchPaintCodePhoto(code, post, shopKey) {
		const cacheKey = shopKey + "::" + code;
		if (paintCodePhotoCache.has(cacheKey)) return paintCodePhotoCache.get(cacheKey);
		const promise = Promise.resolve().then(function () {
			return post({query: `query TouchupPaintCodePhoto($handle: String!) {
				metaobject(handle: {type: "paint_codes", handle: $handle}) {
					code: field(key: "paint_code") { value }
					photo: field(key: "paint_code_image") { reference { ... on MediaImage {
						image { url(transform: {maxWidth: 1000}) altText width height }
					} } }
				}
			}`, variables: {handle: toHandle(code)}});
		}).then(function (json) {
			if (!json || (json.errors && json.errors.length)) throw new Error("Paint code photo unavailable");
			const entry = json.data && json.data.metaobject;
			const image = entry && entry.photo && entry.photo.reference && entry.photo.reference.image;
			return entry && codeKey(entry.code && entry.code.value) === code && image && /^https:\/\//i.test(image.url || "") ? image : null;
		}).catch(function (error) {
			paintCodePhotoCache.delete(cacheKey);
			throw error;
		});
		paintCodePhotoCache.set(cacheKey, promise);
		return promise;
	}
	function fetchCollectionPhotos(handle, codes, post, shopKey) {
		if (!handle || !codes.length) return Promise.resolve({});
		const sortedCodes = codes.slice().sort();
		const cacheKey = [shopKey, handle, sortedCodes.join(",")].join("::");
		if (collectionPhotoCache.has(cacheKey)) return collectionPhotoCache.get(cacheKey);
		const photoHandles = Array.from(new Set(sortedCodes.map(function (code) {
			return handle + "-" + toHandle(code);
		}).concat(handle)));
		const requests = [];
		for (let index = 0; index < photoHandles.length; index += 25) {
			const batch = photoHandles.slice(index, index + 25);
			const variables = {};
			const definitions = batch.map(function (value, position) {
				variables["h" + position] = value;
				return "$h" + position + ": String!";
			});
			const fields = batch.map(function (value, position) {
				return `p${position}: metaobject(handle: {type: "collection_painted_images", handle: $h${position}}) {
					collection: field(key: "collection_title") { value }
					code: field(key: "paint_code") { value }
					photo: field(key: "images") { reference { ... on MediaImage {
						image { url(transform: {maxWidth: 1000}) altText width height }
					} } }
				}`;
			});
			requests.push(Promise.resolve().then(function () {
				return post({query: "query TouchupCollectionPhotos(" + definitions.join(",") + ") {" + fields.join("\n") + "}", variables: variables});
			}));
		}
		const promise = Promise.all(requests).then(function (responses) {
			const images = Object.create(null);
			responses.forEach(function (json) {
				if (!json || (json.errors && json.errors.length)) throw new Error("Collection photos unavailable");
				Object.values(json.data || {}).forEach(function (entry) {
					if (!entry || toHandle(entry.collection && entry.collection.value) !== handle) return;
					const code = codeKey(entry.code && entry.code.value);
					const image = entry.photo && entry.photo.reference && entry.photo.reference.image;
					if (sortedCodes.includes(code) && image && /^https:\/\//i.test(image.url || "") && !images[code]) images[code] = image;
				});
			});
			return images;
		}).catch(function (error) {
			collectionPhotoCache.delete(cacheKey);
			throw error;
		});
		collectionPhotoCache.set(cacheKey, promise);
		return promise;
	}
	function addCollectionPhotos(details, vehicle, post, shopKey, selectedCode) {
		const result = Object.assign({}, details, {images: Object.assign(Object.create(null), details.images)});
		const codes = Array.from(new Set(Object.keys(result.names).concat(Object.keys(result.fallbackImages), selectedCode || []).filter(Boolean)));
		const missingCodes = function () { return codes.filter(function (code) { return !result.images[code]; }); };
		if (!missingCodes().length) return Promise.resolve(result);
		return fetchCollectionPhotos(result.handle, missingCodes(), post, shopKey).catch(function () { return {}; }).then(function (images) {
			Object.assign(result.images, images);
			const base = toHandle([vehicle && vehicle.year, vehicle && vehicle.make, vehicle && vehicle.model].filter(Boolean).join(" "));
			if (!base || !result.handle.startsWith(base + "-") || !missingCodes().length) return result;
			return fetchHandle(base, post, shopKey).then(function (model) {
				const sameColors = missingCodes().filter(function (code) {
					return !name(model, code) || !name(result, code) || name(model, code).toLowerCase() === name(result, code).toLowerCase();
				});
				if (!sameColors.length) return result;
				sameColors.forEach(function (code) {
					if (model && model.images[code]) result.images[code] = model.images[code];
				});
				return fetchCollectionPhotos(base, sameColors.filter(function (code) { return !result.images[code]; }), post, shopKey).catch(function () { return {}; }).then(function (photos) {
					Object.assign(result.images, photos);
					sameColors.forEach(function (code) {
						if (model && !result.fallbackImages[code] && model.fallbackImages[code]) {
							result.fallbackImages = Object.assign({}, result.fallbackImages, {[code]: model.fallbackImages[code]});
						}
					});
					return result;
				});
			});
		}).catch(function () { return result; });
	}
	function name(details, code) {
		return (details && details.names && details.names[codeKey(code)]) || "";
	}
	function label(details, code) {
		const color = name(details, code);
		return String(code || "") + (color ? " \u2014 " + color : "");
	}
	function updateOptions(select, details) {
		if (!select) return;
		Array.from(select.options).forEach(function (option) {
			if (option.value) option.textContent = label(details, option.value);
		});
	}
	const previewJobs = new WeakMap();
	function renderPreview(container, details, code) {
		if (!container) return;
		const photo = details && details.images && details.images[codeKey(code)];
		const previewKey = photo ? [details.handle, codeKey(code), photo.url].join("|") : "";
		if (previewKey && container.dataset.paintPreviewKey === previewKey) return;
		const previous = previewJobs.get(container);
		if (previous) clearTimeout(previous.timer);
		const job = {ready: false, settled: false, timer: null};
		previewJobs.set(container, job);
		container.dataset.paintPreviewKey = previewKey;
		function current() { return previewJobs.get(container) === job; }
		function clear() {
			if (!current()) return;
			clearTimeout(job.timer);
			container.removeAttribute("aria-busy");
			container.hidden = true;
			container.replaceChildren();
			container.dispatchEvent(new Event("partify:paint-preview:visibility", {bubbles: true}));
		}
		if (!photo) { clear(); return; }
		// Preload off the page; retain the current layout while the next photo loads.
		container.setAttribute("aria-busy", "true");
		const image = document.createElement("img");
		image.alt = photo.altText || label(details, code);
		image.decoding = "async";
		if (photo.width && photo.height) { image.width = photo.width; image.height = photo.height; }
		const caption = document.createElement("figcaption");
		caption.textContent = label(details, code);
		function show() {
			if (!current() || !job.ready || !job.settled) return;
			container.replaceChildren(image, caption);
			container.removeAttribute("aria-busy");
			container.hidden = false;
			container.dispatchEvent(new Event("partify:paint-preview:visibility", {bubbles: true}));
		}
		job.timer = setTimeout(function () { job.settled = true; show(); }, 120);
		image.addEventListener("load", function () { job.ready = true; show(); });
		image.addEventListener("error", clear);
		image.src = photo.url;
	}
	window.PartifyTouchupColorDetails = {key: key, load: load, loadNames: loadNames, name: name, label: label, updateOptions: updateOptions, renderPreview: renderPreview};
})();
